import React from 'react';
import { MessageSquare, FileText, Globe, Mail, Search, X, Plus, RefreshCw, CreditCard } from 'lucide-react';
import styled from 'styled-components';

type ContentType = 'message' | 'file' | 'page' | 'email_template' | 'digital_card';

interface TabNavigationProps {
  activeTab: ContentType;
  setActiveTab: (tab: ContentType) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onReset: () => void;
  onAddContent?: () => void;
  onRefresh?: () => void; // Add refresh prop
}

// Modern styled components with Gilroy font
const NavContainer = styled.div`
  width: 100%;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  position: relative;
  z-index: 10;
`;

const NavContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
`;

const NavWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  gap: 24px;
`;

const TabSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const TabList = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(248, 250, 252, 0.8);
  padding: 6px;
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.5);
  backdrop-filter: blur(10px);
`;

const TabButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${props => props.isActive 
    ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' 
    : 'transparent'
  };
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: ${props => props.isActive ? 600 : 500};
  color: ${props => props.isActive ? 'white' : '#64748B'};
  font-size: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  letter-spacing: -0.01em;
  min-width: 100px;
  justify-content: center;
  
  &:hover {
    background: ${props => props.isActive 
      ? 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)' 
      : 'rgba(59, 130, 246, 0.08)'
    };
    color: ${props => props.isActive ? 'white' : '#3B82F6'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2;
    transition: all 0.3s ease;
  }

  ${props => props.isActive && `
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
  `}
`;

const TabCount = styled.span`
  background: rgba(255, 255, 255, 0.2);
  color: inherit;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
  min-width: 20px;
  text-align: center;
`;

const SearchSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchWrapper = styled.div`
  position: relative;
  min-width: 320px;
`;

const SearchContainer = styled.div`
  position: relative;
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  overflow: hidden;

  &:focus-within {
    border-color: #3B82F6;
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.15);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: rgba(59, 130, 246, 0.4);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94A3B8;
  pointer-events: none;
  z-index: 2;
  transition: color 0.3s ease;

  ${SearchContainer}:focus-within & {
    color: #3B82F6;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 44px 0 44px;
  font-size: 14px;
  font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  color: #1F2937;
  background: transparent;
  border: none;
  outline: none;
  letter-spacing: -0.01em;

  &::placeholder {
    color: #94A3B8;
    transition: color 0.3s ease;
  }

  &:focus::placeholder {
    color: #CBD5E1;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(248, 250, 252, 0.8);
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748B;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: ${props => props.style?.display === 'none' ? 0 : 1};
  visibility: ${props => props.style?.display === 'none' ? 'hidden' : 'visible'};

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #EF4444;
    transform: translateY(-50%) scale(1.05);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: -0.01em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 12px rgba(16, 185, 129, 0.3);

  &:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
    stroke-width: 2.5;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: 'Gilroy', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: -0.01em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 12px rgba(99, 102, 241, 0.3);

  &:hover {
    background: linear-gradient(135deg, #4F46E5 0%, #4338CA 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(99, 102, 241, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
    stroke-width: 2.5;
  }
`;

const ContentCounts = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: #64748B;
  font-size: 13px;
  font-family: 'Gilroy', sans-serif;
  font-weight: 500;
`;

const CountItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(248, 250, 252, 0.6);
  border-radius: 8px;
  border: 1px solid rgba(226, 232, 240, 0.4);

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
`;

export const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  setActiveTab,
  searchQuery,
  setSearchQuery,
  onReset,
  onAddContent,
  onRefresh
}) => {
  const tabs = [
    {
      id: 'message',
      label: 'Messages',
      Icon: MessageSquare,
      count: 0 // You can pass this as props or calculate from data
    },
    {
      id: 'file',
      label: 'Files',
      Icon: FileText,
      count: 0
    },
    {
      id: 'page',
      label: 'Pages',
      Icon: Globe,
      count: 0
    },
    {
      id: 'email_template',
      label: 'Email Templates',
      Icon: Mail,
      count: 0
    },
    {
      id: 'digital_card',
      label: 'Digital Cards',
      Icon: CreditCard,
      count: 0
    }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  return (
    <NavContainer>
      <NavContent>
        <NavWrapper>
          <TabSection>
            <TabList>
              {tabs.map(({ id, label, Icon, count }) => (
                <TabButton 
                  key={id}
                  isActive={activeTab === id}
                  onClick={() => setActiveTab(id as ContentType)}
                >
                  <Icon />
                  <span>{label}</span>
                  {count > 0 && <TabCount>{count}</TabCount>}
                </TabButton>
              ))}
            </TabList>
          </TabSection>

          <SearchSection>
            <SearchWrapper>
              <SearchContainer>
                <SearchIconWrapper>
                  <Search size={16} />
                </SearchIconWrapper>
                <SearchInput
                  type="text"
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${activeTabData?.label.toLowerCase() || 'content'}...`}
                />
                <ClearButton 
                  onClick={onReset}
                  style={{ display: searchQuery ? 'flex' : 'none' }}
                >
                  <X size={12} />
                </ClearButton>
              </SearchContainer>
            </SearchWrapper>

            {onRefresh && (
              <RefreshButton onClick={onRefresh}>
                <RefreshCw />
                <span>Refresh</span>
              </RefreshButton>
            )}

            <AddButton onClick={onAddContent}>
              <Plus />
              <span>Add {activeTabData?.label.slice(0, -1) || 'Content'}</span>
            </AddButton>
          </SearchSection>
        </NavWrapper>
      </NavContent>
    </NavContainer>
  );
};

export default TabNavigation;